import { Country } from "country-state-city";
import { FLAG_CODE_OVERRIDE } from "../transfer-windows/CountryFlag";

// Resolve a jurisdiction value to { code, label } for flag-icons rendering.
// The backend `jurisdictions`/`country` values may arrive as an ISO alpha-2
// code ("es"), a full country name ("Spain"), or an object ({ name/code/n }).
// We map all three to a flag-icons CSS code so the directory renders clean,
// consistent flags (the design uses emoji; the platform uses flag-icons).
// Defensive by design: unknown values yield code:null and we simply skip the
// flag, keeping the card/chip complete.
let NAME_TO_ISO = null;
const nameToIso = () => {
  if (NAME_TO_ISO) return NAME_TO_ISO;
  NAME_TO_ISO = {};
  try {
    Country.getAllCountries().forEach((c) => {
      NAME_TO_ISO[c.name.toLowerCase()] = c.isoCode.toLowerCase();
    });
  } catch (e) {
    /* country-state-city unavailable — fall back to override map only */
  }
  return NAME_TO_ISO;
};

export const resolveCountry = (value) => {
  if (!value) return { code: null, label: "" };
  const raw =
    typeof value === "string"
      ? value
      : value.code || value.name || value.n || value.country || "";
  const label =
    typeof value === "string"
      ? value
      : value.name || value.n || value.country || value.code || "";
  if (!raw) return { code: null, label: label || "" };

  // ISO alpha-2 code
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const code = raw.toLowerCase();
    let name = label;
    if (typeof value === "string") {
      const known = Country.getCountryByCode
        ? Country.getCountryByCode(raw.toUpperCase())
        : null;
      name = known?.name || raw.toUpperCase();
    }
    return { code, label: name };
  }

  // Full name -> special-case override, then country-state-city
  const override = FLAG_CODE_OVERRIDE[raw];
  if (override) return { code: override, label: raw };
  const iso = nameToIso()[raw.toLowerCase()];
  return { code: iso || null, label: raw };
};

// Small flag renderer using the already-loaded flag-icons CSS.
export const Flag = ({ code, width = 20, height = 14, label = "" }) => {
  if (!code) return null;
  return (
    <span
      className={`fi fi-${code} inline-block rounded-sm flex-shrink-0`}
      style={{ width, height, boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.08)" }}
      role="img"
      aria-label={label || code}
    />
  );
};
